import React, { useState, useRef, useCallback } from 'react';
import { AttachFile as AttachFileIcon, DeleteOutline as DeleteIcon } from '@mui/icons-material';
import { Button } from '../ui/Button';
import { formatFileSize } from '../../utils/formatFileSize';

// Mirrors the backend's ALLOWED_FEEDBACK_MIMES and multer's 10 MB per-file limit.
const MAX_FILE_SIZE_BYTES = 10 * 1024 * 1024;
const ACCEPTED_MIME_TYPES = [
  'image/jpeg', 'image/png', 'image/gif', 'image/webp',
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'text/plain',
  'application/zip',
  'application/x-zip-compressed'
];
const ACCEPTED_EXTENSIONS = 'image/*,.pdf,.doc,.docx,.xls,.xlsx,.txt,.zip';

interface SessionFeedbackFormProps {
  /** Rejects on failure; the form then keeps what the rep typed and attached. */
  onSubmit: (formData: FormData) => Promise<void>;
  submitting: boolean;
  error: string | null;
}

/**
 * The owner's session feedback (comment + attachments), opened inline at the
 * top of the Salesperson Feedback tab by its "Add Session Feedback" button and
 * closed again on Cancel or a successful submit. Ported from the session-scoped mode of
 * salesguru-frontend's SessionFeedbackModal; it is a card, not a modal.
 */
const SessionFeedbackForm: React.FC<SessionFeedbackFormProps> = ({ onSubmit, submitting, error }) => {
  const [comment, setComment] = useState('');
  const [files, setFiles] = useState<File[]>([]);
  const [fileError, setFileError] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const validateAndAddFiles = useCallback((newFiles: FileList | File[]) => {
    setFileError(null);
    const validated: File[] = [];
    for (const file of Array.from(newFiles)) {
      if (!ACCEPTED_MIME_TYPES.includes(file.type)) {
        setFileError(`"${file.name}" has an unsupported file type.`);
        return;
      }
      if (file.size > MAX_FILE_SIZE_BYTES) {
        setFileError(`"${file.name}" exceeds the 10 MB size limit.`);
        return;
      }
      validated.push(file);
    }
    setFiles(prev => [...prev, ...validated]);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    validateAndAddFiles(e.dataTransfer.files);
  }, [validateAndAddFiles]);

  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      validateAndAddFiles(e.target.files);
      e.target.value = '';
    }
  };

  const removeFile = (index: number) => {
    setFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!comment.trim() || submitting) return;

    const formData = new FormData();
    formData.append('comment', comment.trim());
    files.forEach(file => formData.append('attachments', file));

    try {
      await onSubmit(formData);
    } catch {
      // The page shows the error; keep the input so the rep can retry.
      return;
    }
    setComment('');
    setFiles([]);
    setFileError(null);
  };

  return (
    <form onSubmit={handleSubmit} className="border border-hairline rounded-card p-4 space-y-4" aria-label="Add session feedback">
      <h3 className="heading-4">Add session feedback</h3>

      <div>
        <label htmlFor="session-feedback-comment" className="block text-sm font-medium text-ink-secondary mb-1">
          Comment <span className="text-error">*</span>
        </label>
        <textarea
          id="session-feedback-comment"
          value={comment}
          onChange={e => setComment(e.target.value)}
          placeholder="Share your feedback about this session..."
          rows={4}
          required
          className="w-full border border-ink-border rounded-control px-3 py-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-signature focus:border-transparent"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-ink-secondary mb-1">
          Attachments <span className="text-xs text-ink-muted">(optional, 10 MB max per file)</span>
        </label>
        <div
          onDragOver={e => { e.preventDefault(); setIsDragging(true); }}
          onDragLeave={() => setIsDragging(false)}
          onDrop={handleDrop}
          onClick={() => fileInputRef.current?.click()}
          data-testid="session-feedback-dropzone"
          className={`border-2 border-dashed rounded-control px-4 py-6 text-center cursor-pointer transition-colors ${
            isDragging ? 'border-signature bg-signature-tint' : 'border-ink-border hover:border-ink-muted'
          }`}
        >
          <AttachFileIcon className="mx-auto text-ink-muted mb-1" fontSize="small" />
          <p className="text-xs text-ink-muted">
            Drag files here or <span className="text-signature underline">browse</span>
          </p>
          <p className="text-xs text-ink-muted mt-0.5">Images, PDF, Word, Excel, TXT, ZIP</p>
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept={ACCEPTED_EXTENSIONS}
            onChange={handleFileInput}
            className="hidden"
            data-testid="session-feedback-file-input"
          />
        </div>
      </div>

      {fileError && (
        <p className="text-xs text-error">{fileError}</p>
      )}

      {files.length > 0 && (
        <ul className="space-y-1">
          {files.map((file, index) => (
            <li key={index} className="flex items-center justify-between text-xs text-ink-secondary bg-surface-subtle rounded-inner px-2 py-1">
              <span className="truncate mr-2">{file.name}</span>
              <div className="flex items-center gap-2 flex-shrink-0">
                <span className="text-ink-muted">{formatFileSize(file.size)}</span>
                <button
                  type="button"
                  onClick={() => removeFile(index)}
                  aria-label={`Remove ${file.name}`}
                  className="text-ink-muted hover:text-error"
                >
                  <DeleteIcon fontSize="small" />
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}

      {error && (
        <p className="text-xs text-ink bg-error-tint border border-error rounded-control px-3 py-2" role="alert">
          {error}
        </p>
      )}

      <div className="flex justify-end">
        <Button type="submit" variant="primary" disabled={submitting || !comment.trim()}>
          {submitting ? 'Submitting...' : 'Submit Feedback'}
        </Button>
      </div>
    </form>
  );
};

export default SessionFeedbackForm;
